import { useEffect } from 'react';
import { motion } from 'framer-motion';

interface SuccessMessageProps {
  isOpen: boolean;
  onClose: () => void;
  message: string;
}

export default function SuccessMessage({ isOpen, onClose, message }: SuccessMessageProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="fixed inset-0 z-50 bg-black bg-opacity-50"
            onClick={handleBackdropClick}
          />

          {/* Success Message */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{
              type: 'spring',
              damping: 30,
              stiffness: 300,
              duration: 0.5,
            }}
            className="fixed bottom-0 left-0 right-0 z-50 flex justify-center"
          >
            <div className="mx-0 flex max-h-[90vh] w-full max-w-[768px] flex-col overflow-hidden rounded-tl-[28px] rounded-tr-[28px] border border-zinc-300 bg-white shadow-lg sm:mx-4">
              {/* Handle */}
              <div className="flex w-full items-center justify-center px-4 pt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="h-1.5 w-[52px] cursor-pointer rounded-[20px] bg-zinc-300 transition-colors duration-200 hover:bg-zinc-400"
                  aria-label="Close message"
                />
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto px-4 pb-6">
                <div className="flex w-full flex-col items-center justify-center space-y-4 py-8">
                  {/* Success Icon */}
                  <div className="flex h-16 w-16 items-center justify-center">
                    <img src="/success.svg" alt="Success" />
                  </div>

                  {/* Success Message */}
                  <p className="text-center text-lg font-medium text-[#333333]">{message}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </>
  );
}
